/**
 * Whether this browser has walked a given account through the setup steps.
 *
 * Onboarding is more than a sign-in form: steps 1-3 are where Nudge asks for
 * location, asks for notifications, and states plainly that the web build can
 * only trigger while it is open. Those steps were unreachable — `App` swapped
 * `<Onboarding/>` out the instant `user` became truthy, which `signUp()` does
 * before `setStep(1)` runs — so after a real signup the app went straight to
 * Home and *nothing ever asked for location*. On a phone the passive
 * `watchPosition` still tended to raise a prompt; on a MacBook it often did
 * not, which is exactly what the tester reported (APP-12).
 *
 * The flag lives in `localStorage`, keyed by user id, and that scoping is the
 * point rather than an implementation detail: browser permissions are granted
 * per origin, per browser, so "has been set up" is a fact about *this browser*
 * and cannot come from the server. Signing in on a new device correctly walks
 * through setup again.
 */

const KEY_PREFIX = 'nudge.onboarded.'

function key(userId: string): string {
  return `${KEY_PREFIX}${userId}`
}

/** Storage is unavailable in private modes and with cookies blocked. Treating a
 * failure as "not onboarded" shows the permission prompts again, which is the
 * harmless direction to be wrong in. */
export function hasOnboarded(userId: string | null | undefined): boolean {
  if (!userId) return false
  try {
    return localStorage.getItem(key(userId)) === '1'
  } catch {
    return false
  }
}

export function markOnboarded(userId: string | null | undefined): void {
  if (!userId) return
  try {
    localStorage.setItem(key(userId), '1')
  } catch {
    // Non-fatal: they see the setup steps again next visit.
  }
}
